import React, { useState, useRef, useCallback, useEffect } from 'react';
import './DataList.css';

export interface DataListItem {
  id: string | number;
  title: string;
  subtitle?: string;
  details?: Array<{
    icon?: string;
    text: string;
    className?: string;
  }>;
  metadata?: Array<{
    label: string;
    value: string;
  }>;
  actions?: Array<{
    icon: string;
    label: string;
    onClick: () => void;
  }>;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  onClick?: () => void;
  onContextMenu?: (event: React.MouseEvent) => void;
  onSelect?: () => void; // Для выделения через свайп
  isSelected?: boolean; // Флаг выделения
  className?: string;
  variant?: 'default' | 'compact' | 'detailed';
  isHeader?: boolean;
  status?: 'active' | 'closed' | 'pending';
  icon?: string;
}

export interface DataListGroup {
  id: string | number;
  title: string;
  subtitle?: string;
  items: DataListItem[];
  className?: string;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
}

export interface DataListProps {
  items?: DataListItem[];
  groups?: DataListGroup[];
  className?: string;
  variant?: 'default' | 'compact' | 'detailed';
  showDividers?: boolean;
  headerStyle?: 'default' | 'minimal' | 'none';
  emptyState?: {
    icon: string;
    title: string;
    subtitle?: string;
  };
  loading?: boolean;
  loadingText?: string;
}

// Компонент для элемента списка с поддержкой свайпа
const SwipeableDataListItem: React.FC<{
  item: DataListItem;
  itemClasses: string;
  showDividers?: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}> = ({ item, itemClasses, showDividers, isLast, children }) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [wasSwiped, setWasSwiped] = useState(false);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const itemRef = useRef<HTMLDivElement>(null);

  const handleSwipeStart = useCallback((clientX: number, clientY: number) => {
    if (!item.onSelect) return;
    swipeStartX.current = clientX;
    swipeStartY.current = clientY;
    setIsSwiping(true);
  }, [item.onSelect]);

  const handleSwipeMove = useCallback((clientX: number, clientY: number) => {
    if (!isSwiping || !item.onSelect) return;

    const deltaX = clientX - swipeStartX.current;
    const deltaY = Math.abs(clientY - swipeStartY.current);

    if (deltaY > 10 && Math.abs(deltaX) < 10) {
      setIsSwiping(false);
      return;
    }

    if (deltaX < 0) {
      const offset = Math.max(deltaX, -80);
      setSwipeOffset(offset);
    } else if (swipeOffset < 0) {
      const offset = Math.min(deltaX + swipeOffset, 0);
      setSwipeOffset(offset);
    }
  }, [isSwiping, swipeOffset, item.onSelect]);

  const handleSwipeEnd = useCallback(() => {
    setIsSwiping(false);

    if (swipeOffset < -60 && item.onSelect) {
      setWasSwiped(true);
      
      if (itemRef.current) {
        itemRef.current.style.transition = 'none';
        itemRef.current.style.transform = 'translateX(0px)';
        void itemRef.current.offsetHeight;
      }
      
      setSwipeOffset(0);
      
      setTimeout(() => {
        item.onSelect?.();
        setTimeout(() => {
          if (itemRef.current) {
            itemRef.current.style.transition = '';
          }
          setTimeout(() => {
            setWasSwiped(false);
          }, 100);
        }, 10);
      }, 0);
    } else {
      setSwipeOffset(0);
      setWasSwiped(false);
    }
  }, [swipeOffset, item.onSelect]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    handleSwipeStart(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleSwipeStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isSwiping) {
      handleSwipeMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [isSwiping, handleSwipeMove]);

  const handleTouchEnd = useCallback(() => {
    if (isSwiping) {
      handleSwipeEnd();
    }
  }, [isSwiping, handleSwipeEnd]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) return;
    handleSwipeStart(e.clientX, e.clientY);
  }, [handleSwipeStart]);

  useEffect(() => {
    if (!isSwiping) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleSwipeMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleSwipeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSwiping, handleSwipeMove, handleSwipeEnd]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (wasSwiped) {
      e.preventDefault();
      e.stopPropagation();
      setWasSwiped(false);
      return;
    }
    if (item.onClick) {
      item.onClick();
    }
  }, [wasSwiped, item.onClick]);

  if (!item.onSelect) {
    return (
      <>
        <div
          className={itemClasses}
          onClick={handleClick}
          onContextMenu={item.onContextMenu}
        >
          {children}
        </div>
        {showDividers && !isLast && <div className="data-list__divider"></div>}
      </>
    );
  }

  return (
    <>
      <div className="data-list__item-wrapper">
        {/* Иконка выделения под карточкой */}
        <div 
          className="data-list__swipe-action"
          style={{
            opacity: Math.min(Math.abs(swipeOffset) / 60, 1)
          }}
        >
          <i className={item.isSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}></i>
        </div>
        
        {/* Сама карточка с свайпом */}
        <div
          ref={itemRef}
          className={`${itemClasses} ${item.isSelected ? 'data-list__item--selected' : ''}`}
          data-no-tab-swipe
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: isSwiping ? 'none' : undefined
          }}
          onClick={handleClick}
          onContextMenu={item.onContextMenu}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          {children}
        </div>
      </div>
      {showDividers && !isLast && <div className="data-list__divider"></div>}
    </>
  );
};

const DataList: React.FC<DataListProps> = ({
  items = [],
  groups = [],
  className = '',
  variant = 'default',
  showDividers = true,
  headerStyle = 'default',
  emptyState,
  loading = false,
  loadingText = 'Загрузка...'
}) => {
  // Создаем стабильный ключ из ID групп для сравнения
  const groupsKey = React.useMemo(() => {
    return groups.map(g => `${g.id}:${g.collapsible ? '1' : '0'}:${g.defaultCollapsed ? '1' : '0'}`).join(',');
  }, [groups]);

  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string | number>>(() => {
    return new Set(groups.filter(g => g.collapsible && g.defaultCollapsed).map(g => g.id));
  });

  // Синхронизируем состояние при изменении групп (только при реальном изменении)
  React.useEffect(() => {
    setCollapsedGroups(prev => {
      // Сохраняем пользовательские изменения, но добавляем новые группы по умолчанию
      const next = new Set(prev);
      let hasChanges = false;
      
      groups.forEach(g => {
        if (g.collapsible) {
          if (g.defaultCollapsed && !next.has(g.id)) {
            next.add(g.id);
            hasChanges = true;
          }
        }
      });
      
      // Удаляем группы, которых больше нет
      const currentGroupIds = new Set(groups.map(g => g.id));
      prev.forEach(id => {
        if (!currentGroupIds.has(id)) {
          next.delete(id);
          hasChanges = true;
        }
      });
      
      return hasChanges ? next : prev;
    });
  }, [groupsKey]);

  const toggleGroup = (groupId: string | number) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };
  const listClasses = [
    'data-list',
    `data-list--${variant}`,
    className
  ].filter(Boolean).join(' ');

  const itemClasses = (item: DataListItem) => [
    'data-list__item',
    `data-list__item--${variant}`,
    item.isHeader ? `data-list__item--header data-list__item--header-${headerStyle}` : '',
    item.onClick ? 'data-list__item--clickable' : '',
    item.className || ''
  ].filter(Boolean).join(' ');

  const groupClasses = (group: DataListGroup) => [
    'data-list__group',
    group.className || ''
  ].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={listClasses}>
        <div className="data-list__loading">
          <i className="ri-loader-4-line ri-spin"></i>
          <span>{loadingText}</span>
        </div>
      </div>
    );
  }

  if (groups.length === 0 && items.length === 0) {
    if (emptyState) {
      return (
        <div className={listClasses}>
          <div className="data-list__empty">
            <i className={emptyState.icon}></i>
            <span>{emptyState.title}</span>
            {emptyState.subtitle && <small>{emptyState.subtitle}</small>}
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <div className={listClasses}>
      {/* Рендер групп */}
      {groups.length > 0 && groups.map((group, groupIndex) => {
        const isCollapsed = group.collapsible && collapsedGroups.has(group.id);
        return (
        <div key={group.id} className={groupClasses(group)}>
          <div 
            className={`data-list__group-header ${group.collapsible ? 'data-list__group-header--clickable' : ''}`}
            onClick={group.collapsible ? () => toggleGroup(group.id) : undefined}
            style={group.collapsible ? { cursor: 'pointer' } : undefined}
          >
            {group.leftContent && (
              <div className="data-list__group-left-content">
                {group.leftContent}
              </div>
            )}
            <div className="data-list__group-info">
              <div className="data-list__group-title-row">
                <span className="data-list__group-title">
                  <i className="ri-user-line"></i>
                  {group.title}
                </span>
              </div>
              {group.subtitle && (
                <div className="data-list__group-subtitle">
                  <span className="data-list__group-subtitle-badge">
                    <i className="ri-calendar-check-line"></i>
                    {group.subtitle}
                  </span>
                </div>
              )}
            </div>
            {group.rightContent && (
              <div className="data-list__group-right-content">
                {group.rightContent}
              </div>
            )}
            {group.collapsible && (
              <button
                className="data-list__group-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(group.id);
                }}
                aria-label={isCollapsed ? 'Развернуть' : 'Свернуть'}
              >
                <i className={`ri-arrow-${isCollapsed ? 'down' : 'up'}-s-line`}></i>
              </button>
            )}
          </div>
          
          {!isCollapsed && (
          <div className="data-list__group-items">
            {group.items.map((item, itemIndex) => (
              <SwipeableDataListItem
                key={item.id}
                item={item}
                itemClasses={itemClasses(item)}
                showDividers={showDividers}
                isLast={itemIndex === group.items.length - 1}
              >
                <div className="data-list__item-content">
                    {item.leftContent && (
                      <div className="data-list__item-left-content">
                        {item.leftContent}
                      </div>
                    )}
                    <div className="data-list__item-left">
                      <div className="data-list__item-title">
                        {item.title}
                      </div>
                      {item.subtitle && (
                        <div className="data-list__item-subtitle">
                          {item.subtitle}
                        </div>
                      )}
                      {item.details && item.details.length > 0 && (
                        <div className="data-list__item-details">
                          {item.details.map((detail, detailIndex) => (
                            <span
                              key={detailIndex}
                              className={`data-list__item-detail ${detail.className || ''}`}
                            >
                              {detail.icon && <i className={detail.icon}></i>}
                              {detail.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="data-list__item-right">
                      {item.rightContent}
                      
                      {/* Кнопки действий */}
                      {item.actions && item.actions.length > 0 && (
                        <div className="data-list__item-actions">
                          {item.actions.map((action, actionIndex) => (
                            <button
                              key={actionIndex}
                              className="data-list__action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                action.onClick();
                              }}
                              title={action.label}
                            >
                              <i className={action.icon}></i>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </SwipeableDataListItem>
            ))}
          </div>
          )}
        </div>
        );
      })}

      {/* Рендер обычных элементов */}
      {groups.length === 0 && items.map((item, index) => (
        <SwipeableDataListItem
          key={item.id}
          item={item}
          itemClasses={itemClasses(item)}
          showDividers={showDividers}
          isLast={index === items.length - 1}
        >
          <div className="data-list__item-content">
            {item.leftContent && (
              <div className="data-list__item-left-content">
                {item.leftContent}
              </div>
            )}
            <div className="data-list__item-left">
              <div className="data-list__item-title">
                {item.title}
              </div>
              {item.subtitle && (
                <div className="data-list__item-subtitle">
                  {item.subtitle}
                </div>
              )}
              {item.details && item.details.length > 0 && (
                <div className="data-list__item-details">
                  {item.details.map((detail, detailIndex) => (
                    <span
                      key={detailIndex}
                      className={`data-list__item-detail ${detail.className || ''}`}
                    >
                      {detail.icon && <i className={detail.icon}></i>}
                      {detail.text}
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            <div className="data-list__item-right">
              {item.rightContent}
              
              {/* Кнопки действий */}
              {item.actions && item.actions.length > 0 && (
                <div className="data-list__item-actions">
                  {item.actions.map((action, actionIndex) => (
                    <button
                      key={actionIndex}
                      className="data-list__action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick();
                      }}
                      title={action.label}
                    >
                      <i className={action.icon}></i>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {item.bottomContent && (
            <div className="data-list__item-bottom">
              {item.bottomContent}
            </div>
          )}
        </SwipeableDataListItem>
      ))}
    </div>
  );
};

export default DataList;
